import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBagIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { FREE_SHIPPING_THRESHOLD } from '../../utils/shipping';
import { productPath } from '../../utils/products';
import { QuantityStepper } from '../shop/QuantityStepper';
const ease = [0.23, 1, 0.32, 1] as const;
export function CartDrawer() {
  const {
    isOpen,
    close,
    lines,
    subtotal,
    setQty,
    remove,
    count
  } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(1, subtotal / FREE_SHIPPING_THRESHOLD);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);
  return <AnimatePresence data-id="element-151" data-mp="cjgt8s71bfj0">
      {isOpen && <div className="fixed inset-0 z-50" data-id="element-152" data-mp="hhiftn3o5wz8">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} data-id="element-153" data-mp="hh8mbpjf17os" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Winkelwagen" className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-canvas" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-154" data-mp="3pxafpw9vdoh1">
            <div className="flex h-16 items-center justify-between border-b border-line px-6" data-id="element-155" data-mp="36ypqyavgav6k">
              <h2 className="text-lg font-semibold tracking-tight" data-id="element-156" data-mp="37129oq9199ko">Winkelwagen {count > 0 && <span className="text-muted" data-id="element-157" data-mp="3bwzqsys4ejyi">({count})</span>}</h2>
              <button onClick={close} className="grid h-10 w-10 place-items-center rounded-full hover:bg-surface" aria-label="Sluiten" data-id="element-158" data-mp="1him35toawj6p">
                <XIcon className="h-5 w-5" data-id="element-159" data-mp="1coraw1umqvw0" />
              </button>
            </div>

            {lines.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-160" data-mp="13q5zcqnksh4q">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-surface" data-id="element-161" data-mp="20gg7huehi6ir">
                  <ShoppingBagIcon className="h-6 w-6 text-muted" data-id="element-162" data-mp="1vhc27euoksxp" />
                </span>
                <p className="mt-5 text-lg font-semibold" data-id="element-163" data-mp="1vfitzv4f60nl">Je winkelwagen is leeg</p>
                <p className="mt-1 text-sm text-muted" data-id="element-164" data-mp="1rdxb9t57es5h">Begin met onze bestseller: het complete hammam-ritueel.</p>
                <Link to="/product/hammam-ritueel-set" onClick={close} className="mt-6 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-165" data-mp="1mj01gk03oerj">
                  Bekijk de Hammam Ritueel Set
                </Link>
              </div> : <>
                <div className="border-b border-line px-6 py-4" data-id="element-166" data-mp="2rlu8nppufubs">
                  <p className="text-sm" data-id="element-167" data-mp="2voh2fw62rpbm">
                    {remaining > 0 ? <>Nog <strong data-id="element-168" data-mp="30gih1oqpts4t">{formatPrice(remaining)}</strong> tot gratis verzending</> : <strong className="text-olive" data-id="element-169" data-mp="35ejxe1wrbdcl">Je bestelling wordt gratis verzonden</strong>}
                  </p>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-line" data-id="element-170" data-mp="3ag9b5pbd654d">
                    <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${remaining > 0 ? 'bg-pink' : 'bg-olive'}`} style={{
                width: `${progress * 100}%`
              }} data-id="element-171" data-mp="3fbessbxqgxmn" />
                  </div>
                </div>

                <ul className="flex-1 divide-y divide-line overflow-y-auto px-6" data-id="element-172" data-mp="1p1d8w57tf1dt">
                  {lines.map(l => <li key={`${l.slug}-${l.variantId}`} className="flex gap-4 py-5" data-id="element-173" data-mp="ppbp2uk96px3">
                      <Link to={productPath(l.product)} onClick={close} className="shrink-0" data-id="element-174" data-mp="prefvgt8cn2v">
                        <img src={l.product.image} alt="" className="h-20 w-20 rounded-xl bg-surface object-cover" data-id="element-175" data-mp="13n07zwurfmzi" />
                      </Link>
                      <div className="flex flex-1 flex-col" data-id="element-176" data-mp="aznmzr48zn8b">
                        <div className="flex justify-between gap-3" data-id="element-177" data-mp="ay2q3uugns7a">
                          <div data-id="element-178" data-mp="62x9nbjtv5ql">
                            <p className="font-medium" data-id="element-179" data-mp="5zht04ai9on2">{l.product.name}</p>
                            <p className="text-sm text-muted" data-id="element-180" data-mp="krjnf8tt3pfp">{l.variant.label}</p>
                          </div>
                          <p className="font-medium tabular-nums" data-id="element-181" data-mp="fxp4w8pxtgi7">{formatPrice(l.total)}</p>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3" data-id="element-182" data-mp="3jgiekaflhl73">
                          <QuantityStepper value={l.qty} onChange={q => setQty(l.slug, l.variantId, q)} data-id="element-183" data-mp="1yy6ion93avbf" />
                          <button onClick={() => remove(l.slug, l.variantId)} className="grid h-9 w-9 place-items-center rounded-full text-muted hover:bg-surface hover:text-ink" aria-label={`${l.product.name} verwijderen`} data-id="element-184" data-mp="23omww9kqikyu">
                            <Trash2Icon className="h-4 w-4" data-id="element-185" data-mp="2hpqw2i0nix49" />
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>

                <div className="border-t border-line px-6 py-5" data-id="element-186" data-mp="28nhn3wl17bsz">
                  <div className="flex items-baseline justify-between" data-id="element-187" data-mp="2dmb2jujvpcvs">
                    <span className="text-muted" data-id="element-188" data-mp="x8ugnp7kbrha">Subtotaal</span>
                    <span className="text-xl font-semibold tabular-nums" data-id="element-189" data-mp="x7jbpm2dsdol">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted" data-id="element-190" data-mp="33x3fbvqbmrne">Inclusief btw. Verzendkosten bij het afrekenen.</p>
                  <button onClick={() => {
              close();
              navigate('/afrekenen');
            }} className="mt-5 flex h-14 w-full items-center justify-center rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry" data-id="element-191" data-mp="2yya0nzzasuln">
                    Afrekenen
                  </button>
                  <p className="mt-3 text-center text-xs text-muted" data-id="element-192" data-mp="2g793nhhqn94q">iDEAL · Creditcard · Klarna · PayPal</p>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}